/**
 * How much of the catalog has nutrition yet.
 *
 * WHY SHOW THIS AT ALL
 * --------------------
 * The rankings cover a fraction of the catalog, and a leaderboard that quietly
 * ranks 6% of the shelf looks like it ranks the shelf. Stating the fraction is
 * the difference between a ranking you can trust and one that flatters itself.
 *
 * The bar is segmented by how each value was obtained, because those are not
 * equally strong claims: a published label is not the same as a reference value
 * for the food. Generic sits last and carries the warning colour it has
 * everywhere else in the app.
 */

import { useEffect, useState } from 'react';
import { fetchCoverage, type CoveragePayload } from '@/lib/coverage-source';

const SEGMENTS = [
  { key: 'retailerLabel', label: 'Product label', className: 'seg-label' },
  { key: 'barcodeExact', label: 'Barcode match', className: 'seg-barcode' },
  { key: 'nameMatched', label: 'Matched product', className: 'seg-matched' },
  { key: 'genericReference', label: 'Generic estimate', className: 'seg-generic' },
] as const;

export function CoverageBar(): React.JSX.Element | null {
  const [data, setData] = useState<CoveragePayload | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchCoverage(controller.signal)
      .then((payload) => {
        if (payload !== null) setData(payload);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, []);

  if (data === null || data.total === 0) return null;

  const pct = (v: number): number => (v / data.total) * 100;
  const humanPct =
    data.human.total === 0 ? 0 : (data.human.withNutrition / data.human.total) * 100;
  const petPct = data.pet.total === 0 ? 0 : (data.pet.withNutrition / data.pet.total) * 100;

  return (
    <section className="coverage" aria-label="Nutrition coverage">
      <button
        type="button"
        className="coverage-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="coverage-title">Nutrition data</span>
        <span className="coverage-figure">
          <strong>{data.percent}%</strong>
          <span className="coverage-of">
            {data.withNutrition.toLocaleString()} of {data.total.toLocaleString()}
          </span>
        </span>
        <span className="coverage-chev" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
      </button>

      <div
        className="coverage-track"
        role="img"
        aria-label={`${data.percent}% of ${data.total.toLocaleString()} products have nutrition data`}
      >
        {SEGMENTS.map((s) => {
          const width = pct(data.sources[s.key]);
          if (width <= 0) return null;
          return (
            <span
              key={s.key}
              className={`coverage-seg ${s.className}`}
              style={{ width: `${width}%` }}
              title={`${s.label}: ${data.sources[s.key].toLocaleString()}`}
            />
          );
        })}
      </div>

      {open && (
        <div className="coverage-detail">
          <ul className="coverage-key">
            {SEGMENTS.map((s) => (
              <li key={s.key}>
                <span className={`coverage-dot ${s.className}`} aria-hidden="true" />
                {s.label}
                <strong>{data.sources[s.key].toLocaleString()}</strong>
              </li>
            ))}
          </ul>

          <dl className="coverage-rows">
            <div>
              <dt>People food</dt>
              <dd>
                {data.human.withNutrition.toLocaleString()} of {data.human.total.toLocaleString()}
                <span className="coverage-of"> · {humanPct.toFixed(1)}%</span>
              </dd>
            </div>
            <div>
              <dt>Pet food</dt>
              <dd>
                {data.pet.withNutrition.toLocaleString()} of {data.pet.total.toLocaleString()}
                <span className="coverage-of"> · {petPct.toFixed(1)}%</span>
              </dd>
            </div>
            <div>
              <dt>Label sweep</dt>
              <dd>
                {data.sweep.attempted.toLocaleString()} of {data.sweep.eligible.toLocaleString()}
                <span className="coverage-of"> · {data.sweep.percent}% checked</span>
              </dd>
            </div>
          </dl>

          <p className="coverage-note">
            People food is collected first. “Checked” counts every product we have looked
            up — some carry no published panel, so they finish without gaining data.
          </p>
        </div>
      )}
    </section>
  );
}
